// Areas on the panorama minimap (Advanced Virtual Tour). The minimap shows a
// square window of the site; an area with no scan inside it gets an arrow on
// the window's edge, and "Whole site" fits every scan so each area becomes a
// bubble to click.

import type { TourArea, Vec3 } from './tourAreas';

export interface MinimapView { cx: number; cy: number; half: number }

export interface EdgeArrow {
  key: string;
  // On the window's edge: -1 … 1 across (right +) and up (north +).
  nx: number;
  ny: number;
  // Direction from the window's centre, radians (atan2 of the scene's x/y).
  angle: number;
}

const inside = (v: MinimapView, p: Vec3, margin: number) =>
  Math.abs(p.x - v.cx) <= v.half * (1 - margin) && Math.abs(p.y - v.cy) <= v.half * (1 - margin);

// One arrow per area out of the window, pointing at its scan nearest the window.
export function edgeArrows(view: MinimapView, areas: TourArea[], margin = 0.04): EdgeArrow[] {
  const out: EdgeArrow[] = [];
  for (const a of areas) {
    if (a.stations.some((s) => inside(view, s.position, margin))) continue;
    let best = a.stations[0].position, bestD = Infinity;
    for (const s of a.stations) {
      const d = Math.hypot(s.position.x - view.cx, s.position.y - view.cy);
      if (d < bestD) { bestD = d; best = s.position; }
    }
    const dx = best.x - view.cx, dy = best.y - view.cy;
    const m = Math.max(Math.abs(dx), Math.abs(dy)) || 1;
    out.push({ key: a.key, nx: dx / m, ny: dy / m, angle: Math.atan2(dy, dx) });
  }
  return out;
}

// The square that holds every scan, with room around the outermost ones.
export function siteView(areas: TourArea[], margin = 0.15, minHalf = 10): MinimapView | null {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const a of areas) for (const s of a.stations) {
    minX = Math.min(minX, s.position.x); maxX = Math.max(maxX, s.position.x);
    minY = Math.min(minY, s.position.y); maxY = Math.max(maxY, s.position.y);
  }
  if (!Number.isFinite(minX)) return null;
  const half = Math.max((Math.max(maxX - minX, maxY - minY) / 2) * (1 + margin), minHalf);
  return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, half };
}

// A bubble's size on screen: more scans, a bigger bubble, within limits.
export const bubbleRadius = (scans: number) => Math.min(22, 9 + 2.2 * Math.sqrt(scans));

export interface ChipBox { key: string; left: number; top: number; width: number }

// Places each arrow's chip inside the minimap at its edge point, then slides
// chips that would cover one another along the edge, and keeps them off the
// strip at the bottom where the view switch sits.
export function layoutEdgeChips(
  items: Array<{ key: string; nx: number; ny: number; width: number }>,
  w: number, h: number, chipH = 22, reserveBottom = 34,
): ChipBox[] {
  const pad = 4;
  const maxTop = h - chipH - reserveBottom;
  const boxes = items.map((it) => {
    const fx = it.nx * 0.5 + 0.5, fy = -it.ny * 0.5 + 0.5;
    const left = Math.min(Math.max(fx * w - it.width * fx, pad), w - it.width - pad);
    const top = Math.min(Math.max(fy * h - chipH * fy, pad), maxTop);
    return { key: it.key, left, top, width: it.width };
  }).sort((a, b) => a.top - b.top || a.left - b.left);
  const placed: ChipBox[] = [];
  const hits = (b: ChipBox) => placed.some((p) =>
    b.left < p.left + p.width + 2 && p.left < b.left + b.width + 2 && b.top < p.top + chipH + 2 && p.top < b.top + chipH + 2);
  for (const b of boxes) {
    const start = b.top;
    for (let k = 1; hits(b) && k <= 12; k++) {
      const down = start + k * (chipH + 3);
      const up = start - k * (chipH + 3);
      b.top = down <= maxTop ? down : Math.max(up, pad);
    }
    placed.push(b);
  }
  return placed;
}

// About how wide a chip is: arrow, name and distance at the minimap's type size.
export const chipWidth = (name: string, distance: string) => Math.min(170, 48 + name.length * 6.6 + distance.length * 6.2);
